import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './table.module.scss';

type FooterRef = React.RefObject<HTMLElement | null>;

type StickyTableFooterEntry = {
  id: string;
  order: number;
  enabled: boolean;
  sectionRef: FooterRef;
  focusRef?: FooterRef;
  viewportRef?: FooterRef;
  contentRef?: FooterRef;
  activationRowsPassed?: number;
  renderFooter: () => React.ReactNode;
};

type StickyTableFooterEntryInput = Omit<StickyTableFooterEntry, 'order'> & {
  order?: number;
};

type StickyTableFooterLayout = {
  left: number;
  width: number;
  contentWidth: number;
  scrollLeft: number;
};

type StickyTableFooterRenderState = {
  footerHeight: number;
};

type StickyTableFooterContextValue = {
  enabled: boolean;
  activeEntryId: string | null;
  footerHeight: number;
  registerEntry: (entry: StickyTableFooterEntry) => void;
  unregisterEntry: (id: string) => void;
};

type StickyTableFooterProviderProps = {
  enabled?: boolean;
  children:
    | React.ReactNode
    | ((state: StickyTableFooterRenderState) => React.ReactNode);
};

const StickyTableFooterContext =
  createContext<StickyTableFooterContextValue | null>(null);

let stickyTableFooterOrder = 0;

const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
  if (!el) return null;

  const ionContent = el.closest('ion-content') as any;
  if (ionContent?.shadowRoot) {
    const innerScroll = ionContent.shadowRoot.querySelector('.inner-scroll');
    if (innerScroll) return innerScroll as HTMLElement;
  }

  let parent: HTMLElement | null = el.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

const measureFixedHeaderBottom = (anchor: HTMLElement): number => {
  let maxBottom = 0;

  const check = (el: Element) => {
    const computed = window.getComputedStyle(el as HTMLElement);
    if (computed.position !== 'fixed') return;

    const rect = (el as HTMLElement).getBoundingClientRect();
    if (rect.top <= 2 && rect.height > 0 && rect.bottom > maxBottom) {
      maxBottom = rect.bottom;
    }
  };

  let node: HTMLElement | null = anchor;
  while (node) {
    let sibling = node.previousElementSibling;
    while (sibling) {
      check(sibling);
      sibling.querySelectorAll('*').forEach(check);
      sibling = sibling.previousElementSibling;
    }
    node = node.parentElement;
  }

  return maxBottom;
};

const getViewportState = (
  entry: StickyTableFooterEntry
): StickyTableFooterLayout | null => {
  const viewportEl = entry.viewportRef?.current || entry.sectionRef.current;
  const contentEl = entry.contentRef?.current || viewportEl;
  if (!viewportEl || !contentEl) return null;

  const viewportRect = viewportEl.getBoundingClientRect();
  const contentRect = contentEl.getBoundingClientRect();
  const contentWidth = Math.max(
    contentEl.scrollWidth || 0,
    contentRect.width,
    viewportRect.width
  );

  return {
    left: viewportRect.left,
    width: viewportRect.width,
    contentWidth,
    scrollLeft: viewportEl.scrollLeft || 0,
  };
};

const hasPassedActivationRows = (
  entry: StickyTableFooterEntry,
  activationBoundary: number
) => {
  const rowsToPass = entry.activationRowsPassed || 0;
  if (rowsToPass <= 0) return true;

  const root = entry.contentRef?.current || entry.sectionRef.current;
  const row = root?.querySelectorAll('tbody tr').item(rowsToPass - 1);
  if (!row) return true;

  return row.getBoundingClientRect().bottom <= activationBoundary + 1;
};

const getRenderableChildren = (
  children: StickyTableFooterProviderProps['children'],
  state: StickyTableFooterRenderState
) => {
  if (typeof children === 'function') {
    return children(state);
  }

  return children;
};

export function StickyTableFooterProvider({
  enabled = true,
  children,
}: StickyTableFooterProviderProps) {
  const entriesRef = useRef<Map<string, StickyTableFooterEntry>>(new Map());
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const fixedHeaderBottomRef = useRef(0);
  const hostViewportRef = useRef<HTMLDivElement>(null);
  const [registryRevision, setRegistryRevision] = useState(0);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [activeLayout, setActiveLayout] = useState<StickyTableFooterLayout | null>(
    null
  );
  const [footerHeight, setFooterHeight] = useState(0);
  // Mirrors of state read inside the rAF poll closure (which captures stale state).
  const activeEntryIdRef = useRef<string | null>(null);
  const footerHeightRef = useRef(0);

  useEffect(() => {
    activeEntryIdRef.current = activeEntryId;
  }, [activeEntryId]);

  useEffect(() => {
    footerHeightRef.current = footerHeight;
  }, [footerHeight]);

  const registerEntry = useCallback((entry: StickyTableFooterEntry) => {
    entriesRef.current.set(entry.id, entry);
    setRegistryRevision((value) => value + 1);
  }, []);

  const unregisterEntry = useCallback((id: string) => {
    if (!entriesRef.current.has(id)) return;
    entriesRef.current.delete(id);
    setRegistryRevision((value) => value + 1);
  }, []);

  const contextValue = useMemo<StickyTableFooterContextValue>(
    () => ({
      enabled,
      activeEntryId,
      footerHeight,
      registerEntry,
      unregisterEntry,
    }),
    [enabled, activeEntryId, footerHeight, registerEntry, unregisterEntry]
  );

  useEffect(() => {
    if (!enabled) {
      setActiveEntryId(null);
      setActiveLayout(null);
      setFooterHeight(0);
      return;
    }

    let rafId: number | null = null;
    let refreshTimer: number | null = null;

    const refreshFixedHeader = () => {
      const anyEntry = Array.from(entriesRef.current.values()).find(
        (entry) => entry.enabled && entry.sectionRef.current
      );
      const anchor =
        anyEntry?.focusRef?.current || anyEntry?.sectionRef.current || null;
      if (!anchor) return;

      scrollContainerRef.current =
        scrollContainerRef.current || findScrollContainer(anchor);
      fixedHeaderBottomRef.current = measureFixedHeaderBottom(anchor);
    };

    const update = () => {
      const entries = Array.from(entriesRef.current.values())
        .filter((entry) => entry.enabled && entry.sectionRef.current)
        .sort((a, b) => a.order - b.order);

      if (entries.length === 0) {
        setActiveEntryId((current) => (current === null ? current : null));
        setActiveLayout((current) => (current === null ? current : null));
        return;
      }

      const anyAnchor =
        entries[0].focusRef?.current || entries[0].sectionRef.current || null;
      if (anyAnchor) {
        scrollContainerRef.current =
          scrollContainerRef.current || findScrollContainer(anyAnchor);
        fixedHeaderBottomRef.current = measureFixedHeaderBottom(anyAnchor);
      }

      const scrollTop =
        scrollContainerRef.current?.getBoundingClientRect().top || 0;
      const baseline = Math.max(scrollTop, fixedHeaderBottomRef.current);
      const viewportBottom = window.innerHeight;
      const activationOffset = Math.min(
        280,
        Math.max(180, window.innerHeight * 0.28)
      );
      const activationLine = viewportBottom - activationOffset;

      let nextActive: StickyTableFooterEntry | null = null;

      for (const entry of entries) {
        const section = entry.sectionRef.current;
        if (!section) continue;

        const sectionRect = section.getBoundingClientRect();
        if (sectionRect.bottom <= baseline + 1) {
          continue;
        }

        if (sectionRect.top >= viewportBottom) {
          continue;
        }

        // Dock the footer (stop sticking) once the section's real footer is in
        // view. Hysteresis: while this entry is the active one, require the
        // bottom to drop past a deadband larger than the reserved footer height
        // before docking. Mounting the sticky host shifts layout by ~footerHeight,
        // which would otherwise flip this test every frame -> a mount/unmount
        // feedback loop (the visible freeze/judder on the dock boundary).
        const isActiveEntry = activeEntryIdRef.current === entry.id;
        const dockDeadband = isActiveEntry ? footerHeightRef.current + 8 : 0;
        if (sectionRect.bottom <= viewportBottom + 1 - dockDeadband) {
          continue;
        }

        if (sectionRect.top > activationLine) {
          break;
        }

        if (!hasPassedActivationRows(entry, viewportBottom)) {
          break;
        }

        nextActive = entry;
        break;
      }

      if (!nextActive) {
        setActiveEntryId((current) => (current === null ? current : null));
        setActiveLayout((current) => (current === null ? current : null));
        return;
      }

      const nextLayout = getViewportState(nextActive);
      if (!nextLayout || nextLayout.width <= 0) {
        setActiveEntryId((current) => (current === null ? current : null));
        setActiveLayout((current) => (current === null ? current : null));
        return;
      }

      setActiveEntryId((current) =>
        current === nextActive.id ? current : nextActive.id
      );

      setActiveLayout((current) => {
        if (
          current &&
          current.left === nextLayout.left &&
          current.width === nextLayout.width &&
          current.contentWidth === nextLayout.contentWidth &&
          current.scrollLeft === nextLayout.scrollLeft
        ) {
          return current;
        }

        return nextLayout;
      });
    };

    refreshFixedHeader();
    refreshTimer = window.setInterval(refreshFixedHeader, 1000);

    const poll = () => {
      update();
      rafId = window.requestAnimationFrame(poll);
    };

    rafId = window.requestAnimationFrame(poll);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (refreshTimer !== null) window.clearInterval(refreshTimer);
    };
  }, [enabled, registryRevision]);

  useLayoutEffect(() => {
    const host = hostViewportRef.current;
    if (!enabled || !host) {
      if (footerHeight !== 0) setFooterHeight(0);
      return;
    }

    const updateHeight = () => {
      const height = Math.round(host.getBoundingClientRect().height);
      setFooterHeight((current) => (current === height ? current : height));
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(host);

    return () => {
      observer.disconnect();
    };
  }, [enabled, activeEntryId, registryRevision]);

  const activeEntry =
    activeEntryId !== null ? entriesRef.current.get(activeEntryId) || null : null;

  return (
    <StickyTableFooterContext.Provider value={contextValue}>
      {getRenderableChildren(children, { footerHeight })}
      {enabled && activeEntry && activeLayout ? (
        <div
          slot="fixed"
          className={styles.Table__StickyFooterHost}
          aria-hidden="false"
        >
          <div
            ref={hostViewportRef}
            className={styles.Table__StickyFooterViewport}
            style={{
              left: `${activeLayout.left}px`,
              width: `${activeLayout.width}px`,
            }}
          >
            <div
              className={styles.Table__StickyFooterInner}
              style={{
                width: `${activeLayout.contentWidth}px`,
                transform: `translateX(${-activeLayout.scrollLeft}px)`,
              }}
            >
              {activeEntry.renderFooter()}
            </div>
          </div>
        </div>
      ) : null}
    </StickyTableFooterContext.Provider>
  );
}

export function useStickyTableFooterEntry(
  entry: StickyTableFooterEntryInput | null
) {
  const context = useContext(StickyTableFooterContext);
  const orderRef = useRef<number>(++stickyTableFooterOrder);

  useLayoutEffect(() => {
    if (!context?.enabled || !entry) return;

    context.registerEntry({
      ...entry,
      order: entry.order ?? orderRef.current,
    });

    return () => {
      context.unregisterEntry(entry.id);
    };
  }, [
    context?.enabled,
    context?.registerEntry,
    context?.unregisterEntry,
    entry?.id,
    entry?.enabled,
    entry?.sectionRef,
    entry?.focusRef,
    entry?.viewportRef,
    entry?.contentRef,
    entry?.activationRowsPassed,
    entry?.renderFooter,
    entry?.order,
  ]);

  return (
    !!entry && context?.enabled === true && context.activeEntryId === entry.id
  );
}

export function useStickyTableFooterState() {
  const context = useContext(StickyTableFooterContext);

  return {
    enabled: context?.enabled ?? false,
    footerHeight: context?.footerHeight ?? 0,
  };
}
